import {useQuery} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {Box, Group, SimpleGrid, Stack, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import {isMcpAiConnectorsFeatureEnabled} from 'llm/isMcpAiConnectorsFeatureEnabled';
import OmniboxFilterTabs, {type Tab} from 'omnibox/OmniboxFilterTabs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import Modal from 'ui-library/modals/Modal';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';

import {ListExtensions, ListExtensionsVariables} from '../__generated/ListExtensions';
import {LIST_EXTENSIONS_QUERY} from '../ExtensionQueries';
import ExtensionInstallWizard from '../install-wizard/ExtensionInstallWizard';
import ExtensionCard from './ExtensionCard';
import {
  formatRegistryLabelKey,
  type Extension,
  type ExtensionLabelSpec,
  EXTENSION_TYPE_LABELS_PLURAL,
} from './extensionConstants';
import ExtensionDetails, {ExtensionDetailsFooter} from './ExtensionDetails';

/** Plural label for the active tab — matches tab labels ("AI Connectors", "Scientific Models", …). */
function extensionTypePluralLabel(extensionType: ExtensionType): string {
  return EXTENSION_TYPE_LABELS_PLURAL[extensionType];
}

function BrowseTitle({activeTab}: {activeTab: ExtensionType}): JSX.Element {
  return (
    <span className={css(styles.browseTitle)}>
      Browse
      <span className={css(styles.browseTitleCategory)}>{extensionTypePluralLabel(activeTab)}</span>
    </span>
  );
}

function BackButton({onBack}: {onBack: () => void}): JSX.Element {
  return (
    <span className={css(styles.headerBack)}>
      <Button
        text="Back"
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        size={ButtonSize.SMALL}
        icon="arrow-left"
        onClick={onBack}
        className={css(styles.backButtonIconSpacing)}
      />
    </span>
  );
}

interface ContentStoreModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: (result: unknown) => void | Promise<void>;
  onInstall?: (installedExtension: Extension) => void;
  modalKey?: string;
  extensionType?: ExtensionType;
}

const CONTENT_STORE_MODAL_WIDTH = 650;

function buildTabConfig(): Array<Tab<ExtensionType>> {
  const tabs: Array<Tab<ExtensionType>> = [];
  if (isMcpAiConnectorsFeatureEnabled()) {
    tabs.push({id: ExtensionType.MCP_SERVER, label: extensionTypePluralLabel(ExtensionType.MCP_SERVER)});
  }
  if (BenchlingConfig.ENABLE_EXTENSION_TYPE_MODELS) {
    tabs.push({id: ExtensionType.MODEL, label: extensionTypePluralLabel(ExtensionType.MODEL)});
  }
  if (BenchlingConfig.ENABLE_EXTENSION_TYPE_AGENT_SKILLS) {
    tabs.push({id: ExtensionType.AGENT_SKILL, label: extensionTypePluralLabel(ExtensionType.AGENT_SKILL)});
  }
  return tabs;
}

export default function ContentStoreModal({
  onRequestClose,
  onRequestSubmit,
  onInstall,
  extensionType,
}: ContentStoreModalProps): JSX.Element {
  const tabConfig = useMemo(() => buildTabConfig(), []);
  const defaultTab = extensionType ?? tabConfig[0]?.id ?? ExtensionType.MCP_SERVER;
  const [activeTab, setActiveTab] = useState<ExtensionType>(defaultTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegistryLabelFilters, setSelectedRegistryLabelFilters] = useState<Map<string, Set<string>>>(
    () => new Map()
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState<Extension | null>(null);
  const [installingExtension, setInstallingExtension] = useState<Extension | null>(null);

  const variables: ListExtensionsVariables = {extensionType: activeTab};
  const {data, loading, error} = useQuery<ListExtensions, ListExtensionsVariables>(LIST_EXTENSIONS_QUERY, {
    variables,
    fetchPolicy: 'network-only',
  });

  const extensions = useMemo(
    () => data?.extensionRegistryListing?.items ?? [],
    [data?.extensionRegistryListing?.items]
  );

  const labelSpecs: ReadonlyArray<ExtensionLabelSpec> = data?.extensionLabelSpecs ?? [];

  /**
   * For the filter dropdown: per label key, the distinct values that appear on at least one extension
   * in the current list (only options that exist in the registry data, not every theoretical enum value).
   */
  const labelOptionsByKey = useMemo(() => {
    const byKey = new Map<string, Set<string>>();
    for (const ext of extensions) {
      for (const lab of ext.labels) {
        if (!byKey.has(lab.key)) {
          byKey.set(lab.key, new Set());
        }
        byKey.get(lab.key)!.add(lab.value);
      }
    }
    return byKey;
  }, [extensions]);

  const labelKeysSorted = useMemo(
    () => Array.from(labelOptionsByKey.keys()).sort((a, b) => a.localeCompare(b)),
    [labelOptionsByKey]
  );

  const hasRegistryLabelFilters = labelKeysSorted.length > 0;

  const activeFilterCount = useMemo(
    () => Array.from(selectedRegistryLabelFilters.values()).reduce((sum, values) => sum + values.size, 0),
    [selectedRegistryLabelFilters]
  );

  const filteredExtensions = useMemo(() => {
    if (!searchQuery && activeFilterCount === 0) {
      return extensions;
    }
    return extensions.filter((ext) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const name = ext.displayName ?? ext.id;
        const tagLine = ext.tagLine ?? '';
        if (!name.toLowerCase().includes(query) && !tagLine.toLowerCase().includes(query)) {
          return false;
        }
      }

      if (activeFilterCount > 0) {
        for (const [filterKey, filterValues] of selectedRegistryLabelFilters) {
          const match = ext.labels.some((l) => l.key === filterKey && filterValues.has(l.value));
          if (!match) {
            return false;
          }
        }
      }

      return true;
    });
  }, [extensions, searchQuery, activeFilterCount, selectedRegistryLabelFilters]);

  const handleTabChange = useCallback((tab: ExtensionType) => {
    setActiveTab(tab);
    setSearchQuery('');
    setSelectedRegistryLabelFilters(new Map());
    setIsFilterOpen(false);
  }, []);

  const handleToggleLabelFilter = useCallback((labelKey: string, labelValue: string) => {
    setSelectedRegistryLabelFilters((prev) => {
      const next = new Map(prev);
      const values = new Set(next.get(labelKey));
      if (values.has(labelValue)) {
        values.delete(labelValue);
      } else {
        values.add(labelValue);
      }
      if (values.size > 0) {
        next.set(labelKey, values);
      } else {
        next.delete(labelKey);
      }
      return next;
    });
  }, []);

  const labelValueDisplayName = useCallback(
    (labelKey: string, labelValue: string): string => {
      for (const ext of extensions) {
        const match = ext.labels.find((l) => l.key === labelKey && l.value === labelValue);
        if (match != null) {
          return match.displayName;
        }
      }
      return labelValue;
    },
    [extensions]
  );

  const handleSelectExtension = useCallback((ext: Extension) => {
    setSelectedExtension(ext);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedExtension(null);
  }, []);

  const handleBeginInstallConfig = useCallback(() => {
    if (selectedExtension != null) {
      setInstallingExtension(selectedExtension);
    }
  }, [selectedExtension]);

  const handleBackToDetails = useCallback(() => {
    setInstallingExtension(null);
  }, []);

  const handleInstalled = useCallback(
    (installedExtension: Extension) => {
      setInstallingExtension(null);
      setSelectedExtension(null);
      onInstall?.(installedExtension);
      onRequestClose();
    },
    [onInstall, onRequestClose]
  );

  const renderContent = (): JSX.Element => {
    if (loading) {
      return <div className={css(styles.loadingBar)} />;
    }

    if (error) {
      const label = extensionTypePluralLabel(activeTab);
      return (
        <Box p="md">
          <Text>
            Error loading {label}: {error.message}
          </Text>
        </Box>
      );
    }

    if (filteredExtensions.length === 0) {
      return (
        <Box p="md">
          <Text>No {extensionTypePluralLabel(activeTab)} found</Text>
        </Box>
      );
    }

    return (
      <SimpleGrid cols={2} spacing={16} mt="sm">
        {filteredExtensions.map((ext) => (
          <ExtensionCard
            key={ext.id}
            extension={ext}
            labelSpecs={labelSpecs}
            onClick={() => {
              handleSelectExtension(ext);
            }}
          />
        ))}
      </SimpleGrid>
    );
  };

  if (installingExtension != null) {
    return (
      <Modal
        title={<BackButton onBack={handleBackToDetails} />}
        className={css(styles.modal)}
        onRequestClose={onRequestClose}
        bodyPadded={false}
      >
        <Box p="xl" className={css(styles.installModalBody)}>
          <ExtensionInstallWizard
            extension={installingExtension}
            onCancel={handleBackToDetails}
            onInstalled={handleInstalled}
          />
        </Box>
      </Modal>
    );
  }

  if (selectedExtension) {
    return (
      <Modal
        title={<BackButton onBack={handleBackToList} />}
        className={css(styles.modal)}
        onRequestClose={onRequestClose}
        bodyPadded={false}
      >
        <Box className={css(styles.detailsModalBody)}>
          <Box className={css(styles.detailsModalScroll)} p="xl" pb={0}>
            <ExtensionDetails
              extension={selectedExtension}
              labelSpecs={labelSpecs}
              onInstall={handleInstalled}
              onBeginInstallConfig={handleBeginInstallConfig}
            />
          </Box>
          <Box px={48} className={css(styles.detailsFooter)}>
            <ExtensionDetailsFooter extension={selectedExtension} />
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      title={<BrowseTitle activeTab={activeTab} />}
      className={css(styles.modal)}
      onRequestClose={onRequestClose}
      bodyPadded={false}
      showHeaderBorder={false}
    >
      <Box className={css(styles.modalBodyScroll)}>
        <Box pt="md" px="md">
          <Box className={css(styles.bannerWrapper)}>
            <img
              src={staticURL('/static/img/extensions/ui_static/Banner.png')}
              alt=""
              className={css(styles.bannerImg)}
            />
          </Box>
        </Box>

        <OmniboxFilterTabs
          tabs={tabConfig}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className={css(styles.tabs)}
        />

        <Box px="md" pb="lg" pt="md">
          <Stack spacing="md">
            <Group spacing="sm" align="center" noWrap={true}>
              <Box className={css(styles.searchRow)}>
                <PlainSearchInput
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  onEscape={() => {
                    setSearchQuery('');
                  }}
                  borderRadius={BorderRadius.LARGE}
                  shouldAutoSelect={false}
                  className={css(styles.searchInput)}
                />
              </Box>

              {hasRegistryLabelFilters && (
                <Dropdown
                  menu={
                    <Menu scrollable={false} width={Menu.WIDTHS.MEDIUM}>
                      {labelKeysSorted.map((labelKey) => {
                        const values = labelOptionsByKey.get(labelKey);
                        if (values == null || values.size === 0) {
                          return null;
                        }
                        const sortedValues = Array.from(values).sort((a, b) => a.localeCompare(b));
                        return (
                          <Section key={labelKey}>
                            <SectionHeader>{formatRegistryLabelKey(labelKey, labelSpecs)}</SectionHeader>
                            {sortedValues.map((labelValue) => {
                              const isSelected =
                                selectedRegistryLabelFilters.get(labelKey)?.has(labelValue) ?? false;
                              return (
                                <Option
                                  key={labelValue}
                                  value={labelValue}
                                  icon={isSelected ? 'check' : undefined}
                                  onSelect={() => {
                                    handleToggleLabelFilter(labelKey, labelValue);
                                  }}
                                >
                                  {labelValueDisplayName(labelKey, labelValue)}
                                </Option>
                              );
                            })}
                          </Section>
                        );
                      })}
                    </Menu>
                  }
                  isOpen={isFilterOpen}
                  onToggle={setIsFilterOpen}
                  closeOnInsideClick={false}
                >
                  <Button
                    text={activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
                    buttonStyle={
                      activeFilterCount > 0 ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_GRAY
                    }
                    size={ButtonSize.SMALL}
                    isDropdown={true}
                  />
                </Dropdown>
              )}
            </Group>

            <div id="extension-tab-content" role="tabpanel" aria-labelledby={`extension-tab-${activeTab}`}>
              {renderContent()}
            </div>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}

const styles = StyleSheet.create({
  browseTitle: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 10,
  },
  browseTitleCategory: {
    fontSize: 13,
    fontWeight: 400,
    color: Color.GRAY_600,
    letterSpacing: 0.3,
  },
  modal: {
    ':nth-child(1n) .modal-dialog': {
      width: CONTENT_STORE_MODAL_WIDTH,
    },
  },
  bannerWrapper: {
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
    flexShrink: 0,
  },
  bannerImg: {
    width: '100%',
    height: 'auto',
    display: 'block',
    verticalAlign: 'middle',
  },
  headerBack: {
    display: 'inline-flex',
    margin: 0,
  },
  backButtonIconSpacing: {
    border: 'none',
    ':nth-child(1n) .button-wrapper': {
      gap: 8,
    },
  },
  tabs: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '8px 16px',
    borderBottom: '1px solid #e9ecef',
  },
  searchRow: {
    height: 28,
    maxWidth: 300,
    display: 'flex',
  },
  searchInput: {
    height: '100%',
    flex: 1,
    minWidth: 0,
  },
  detailsModalBody: {
    display: 'flex',
    flexDirection: 'column',
    height: 600,
  },
  installModalBody: {
    display: 'flex',
    flexDirection: 'column',
    height: 600,
  },
  detailsModalScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  detailsFooter: {
    flexShrink: 0,
  },
  modalBodyScroll: {
    height: 600,
    overflowY: 'auto',
  },
  loadingBar: {
    height: 2,
    width: '100%',
    backgroundColor: Color.GRAY_200,
    overflow: 'hidden',
    position: 'relative',
    marginTop: 8,
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '40%',
      backgroundColor: Color.BLUE_450,
      animationName: {
        '0%': {transform: 'translateX(-100%)'},
        '100%': {transform: 'translateX(250%)'},
      },
      animationDuration: '1.2s',
      animationTimingFunction: 'ease-in-out',
      animationIterationCount: 'infinite',
    },
  },
});
